import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { Upload, User, Building2, X, Plus, BadgeCheck, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { professionalCategories, companySectors, italianProvinces, companySizes } from '@/lib/constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [skillInput, setSkillInput] = useState(''); // Separate state for skill input
  const [verificationMessage, setVerificationMessage] = useState('');
  const [requestingVerification, setRequestingVerification] = useState(false);

  // Use imported constants
  const sectors = companySectors;

  const countries = ['Italia', 'Altro'];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const endpoint = user.user_type === 'professional' 
        ? `/profiles/professional/${user.id}`
        : `/profiles/company/${user.id}`;
      
      const response = await axios.get(`${API}${endpoint}`);
      setProfile(response.data);
      setFormData(response.data);
    } catch (error) {
      // Profile doesn't exist yet
      if (user.user_type === 'professional') {
        setFormData({
          professional_category: '',
          sectors: [],
          skills: [],
          experience_years: 0,
          description: '',
          hourly_rate: '',
          website: '',
          email_contact: '',
          phone: '',
          linkedin_url: '',
          facebook_url: '',
          instagram_url: '',
          // Albo Professionale
          iscrizione_albo: false,
          albo_dettaglio: '',
          // Location fields
          work_country: 'Italia',
          work_province: '',
          work_city: '',
          available_for_travel: false,
          available_remote: false
        });
      } else {
        setFormData({
          company_name: user.name,
          partita_iva: '',
          sector: '',
          company_size: '',
          description: '',
          website: '',
          email_contact: '',
          phone: '',
          contact_person: '',
          linkedin_url: '',
          facebook_url: '',
          instagram_url: '',
          // Legal headquarters
          legal_country: 'Italia',
          legal_province: '',
          legal_city: '',
          // Operational headquarters
          has_operational_hq: false,
          operational_country: '',
          operational_province: '',
          operational_city: ''
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation for companies: P.IVA is required
    if (user.user_type === 'company' && !formData.partita_iva?.trim()) {
      toast.error('P.IVA / Codice Fiscale è obbligatorio per le aziende');
      return;
    }
    
    // Validation for professionals with Albo: details required
    if (user.user_type === 'professional' && formData.iscrizione_albo && !formData.albo_dettaglio?.trim()) {
      toast.error('Inserisci i dettagli dell\'iscrizione all\'albo professionale');
      return;
    }
    
    setSaving(true);
    try {
      const endpoint = user.user_type === 'professional'
        ? '/profiles/professional'
        : '/profiles/company';
      
      await axios.put(`${API}${endpoint}`, formData);
      toast.success('Profilo aggiornato con successo!');
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Errore nel salvare il profilo');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestVerification = async () => {
    setRequestingVerification(true);
    try {
      await axios.post(`${API}/profiles/professional/request-verification`, {
        message: verificationMessage
      });
      toast.success('Richiesta di verifica inviata! Sarai contattato a breve.');
      setVerificationMessage('');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della richiesta');
    } finally {
      setRequestingVerification(false);
    }
  };

  const handleCancelVerificationRequest = async () => {
    try {
      await axios.delete(`${API}/profiles/professional/cancel-verification-request`);
      toast.success('Richiesta di verifica annullata');
      fetchProfile();
    } catch (error) {
      toast.error('Errore nell\'annullamento della richiesta');
    }
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      let endpoint;
      if (type === 'cv') {
        endpoint = '/profiles/professional/cv-upload';
      } else if (type === 'photo') {
        endpoint = '/profiles/professional/photo-upload';
      } else if (type === 'company_profile') {
        endpoint = '/profiles/company/profile-upload';
      } else if (type === 'logo') {
        endpoint = '/profiles/company/logo-upload';
      }
      
      await axios.post(`${API}${endpoint}`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File caricato con successo!');
      fetchProfile();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Errore nel caricamento del file');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-8 mt-20">
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-3xl md:text-4xl font-playfair font-bold text-[#344B71] mb-2" data-testid="profile-title">
            Il Mio Profilo
          </h1>
          <p className="text-slate-600">
            {user.user_type === 'professional' 
              ? 'Completa il tuo profilo per aumentare le tue opportunità'
              : 'Aggiungi le informazioni sulla tua azienda'}
          </p>
        </motion.div>

        <Card className="p-8 bg-white">
          {user.user_type === 'professional' ? (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 pb-6 border-b border-slate-200">
                <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#344B71]">{user.name}</h2>
                  <p className="text-slate-600">{user.email}</p>
                </div>
              </div>

              <div>
                <Label htmlFor="category" className="text-sm font-medium text-slate-700">
                  Categoria Professionale *
                </Label>
                <Select 
                  value={formData.professional_category} 
                  onValueChange={(value) => setFormData({...formData, professional_category: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="category-select">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {professionalCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="sectors" className="text-sm font-medium text-slate-700">
                  Settori di Competenza *
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {sectors.map((sector) => (
                    <button
                      key={sector}
                      type="button"
                      onClick={() => {
                        const current = formData.sectors || [];
                        if (current.includes(sector)) {
                          setFormData({...formData, sectors: current.filter(s => s !== sector)});
                        } else {
                          setFormData({...formData, sectors: [...current, sector]});
                        }
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        (formData.sectors || []).includes(sector)
                          ? 'bg-[#344B71] text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                      data-testid={`sector-${sector}`}
                    >
                      {sector}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="skills" className="text-sm font-medium text-slate-700">
                  Competenze
                </Label>
                <p className="text-xs text-slate-500 mb-2">Scrivi una competenza e premi Invio o clicca + per aggiungerla</p>
                
                {/* Display current skills as tags */}
                <div className="flex flex-wrap gap-2 mb-2">
                  {(formData.skills || []).map((skill, i) => (
                    <span 
                      key={i} 
                      className="inline-flex items-center px-3 py-1 bg-[#344B71]/10 text-[#344B71] rounded-full text-sm"
                    >
                      {skill}
                      <button
                        type="button"
                        onClick={() => {
                          const newSkills = [...(formData.skills || [])];
                          newSkills.splice(i, 1);
                          setFormData({...formData, skills: newSkills});
                        }}
                        className="ml-2 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                
                {/* Input for new skill */}
                <div className="flex gap-2">
                  <Input
                    id="skills"
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        const newSkill = skillInput.trim();
                        if (newSkill && !(formData.skills || []).includes(newSkill)) {
                          setFormData({...formData, skills: [...(formData.skills || []), newSkill]});
                          setSkillInput('');
                        }
                      }
                    }}
                    placeholder="es. Project Management, Business Analysis..."
                    className="flex-1 h-12"
                    data-testid="skills-input"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const newSkill = skillInput.trim();
                      if (newSkill && !(formData.skills || []).includes(newSkill)) {
                        setFormData({...formData, skills: [...(formData.skills || []), newSkill]});
                        setSkillInput('');
                      }
                    }}
                    className="h-12 px-4 bg-[#344B71] hover:bg-[#344B71]"
                  >
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="experience" className="text-sm font-medium text-slate-700">
                  Anni di Esperienza *
                </Label>
                <Input
                  id="experience"
                  type="number"
                  value={formData.experience_years || 0}
                  onChange={(e) => setFormData({...formData, experience_years: parseInt(e.target.value)})}
                  className="mt-1 h-12"
                  data-testid="experience-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                  Descrizione Professionale *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  placeholder="Racconta la tua esperienza e le tue competenze..."
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              <div>
                <Label htmlFor="hourly_rate" className="text-sm font-medium text-slate-700">
                  Tariffa Oraria (opzionale)
                </Label>
                <Input
                  id="hourly_rate"
                  value={formData.hourly_rate || ''}
                  onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                  placeholder="es. 50-100€/ora"
                  className="mt-1 h-12"
                  data-testid="hourly-rate-input"
                />
              </div>

              {/* Albo Professionale Section */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#344B71] mb-4">Albo Professionale</h3>
                <p className="text-sm text-slate-600 mb-4">Indica se sei iscritto ad un albo professionale</p>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <Label className="text-sm font-medium text-slate-700">Iscrizione Albo Professionale</Label>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="iscrizione_albo"
                          checked={formData.iscrizione_albo === true}
                          onChange={() => setFormData({...formData, iscrizione_albo: true})}
                          className="w-4 h-4 text-[#344B71] focus:ring-[#344B71]"
                        />
                        <span className="text-sm text-slate-700">Sì</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="iscrizione_albo"
                          checked={formData.iscrizione_albo === false || formData.iscrizione_albo === undefined}
                          onChange={() => setFormData({...formData, iscrizione_albo: false, albo_dettaglio: ''})}
                          className="w-4 h-4 text-[#344B71] focus:ring-[#344B71]"
                        />
                        <span className="text-sm text-slate-700">No</span>
                      </label>
                    </div>
                  </div>
                  
                  {formData.iscrizione_albo && (
                    <div>
                      <Label htmlFor="albo_dettaglio" className="text-sm font-medium text-slate-700">
                        Dettagli Iscrizione Albo *
                      </Label>
                      <Textarea
                        id="albo_dettaglio"
                        value={formData.albo_dettaglio || ''}
                        onChange={(e) => setFormData({...formData, albo_dettaglio: e.target.value})}
                        rows={3}
                        placeholder="Indica il nome dell'albo, numero di iscrizione, sezione, data iscrizione, etc."
                        className="mt-1"
                        data-testid="albo-dettaglio-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Es: "Ordine dei Commercialisti di Milano - N. 12345 - Sezione A - dal 2015"
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Location Section for Professionals */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#344B71] mb-4">Sede Operativa</h3>
                <p className="text-sm text-slate-600 mb-4">Indica dove svolgi la tua attività professionale</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Paese *</Label>
                    <Select 
                      value={formData.work_country || 'Italia'} 
                      onValueChange={(value) => setFormData({...formData, work_country: value})}
                    >
                      <SelectTrigger className="mt-1 h-12">
                        <SelectValue placeholder="Seleziona paese" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Provincia *</Label>
                    <Select 
                      value={formData.work_province || ''} 
                      onValueChange={(value) => setFormData({...formData, work_province: value})}
                    >
                      <SelectTrigger className="mt-1 h-12">
                        <SelectValue placeholder="Seleziona provincia" />
                      </SelectTrigger>
                      <SelectContent>
                        {italianProvinces.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Comune *</Label>
                    <Input
                      value={formData.work_city || ''}
                      onChange={(e) => setFormData({...formData, work_city: e.target.value})}
                      placeholder="es. Milano"
                      className="mt-1 h-12"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center space-x-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.available_for_travel || false}
                      onChange={(e) => setFormData({...formData, available_for_travel: e.target.checked})}
                      className="w-5 h-5 rounded border-slate-300 text-[#344B71] focus:ring-[#344B71]"
                    />
                    <span className="text-slate-700">Disponibile per trasferte di lavoro</span>
                  </label>
                  <label className="flex items-center space-x-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.available_remote || false}
                      onChange={(e) => setFormData({...formData, available_remote: e.target.checked})}
                      className="w-5 h-5 rounded border-slate-300 text-[#344B71] focus:ring-[#344B71]"
                    />
                    <span className="text-slate-700">Disponibile per lavoro da remoto</span>
                  </label>
                </div>
              </div>

              {/* Nuovi campi contatti e social */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#344B71] mb-4">Contatti e Social</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="website" className="text-sm font-medium text-slate-700">
                      Sito Web
                    </Label>
                    <Input
                      id="website"
                      type="url"
                      value={formData.website || ''}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      placeholder="https://www.tuosito.com"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email_contact" className="text-sm font-medium text-slate-700">
                      Email di Contatto
                    </Label>
                    <Input
                      id="email_contact"
                      type="email"
                      value={formData.email_contact || ''}
                      onChange={(e) => setFormData({...formData, email_contact: e.target.value})}
                      placeholder="contatto@esempio.com"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Telefono
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+39 123 456 7890"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="linkedin_url" className="text-sm font-medium text-slate-700">
                      LinkedIn
                    </Label>
                    <Input
                      id="linkedin_url"
                      type="url"
                      value={formData.linkedin_url || ''}
                      onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                      placeholder="https://www.linkedin.com/in/tuoprofilo"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="facebook_url" className="text-sm font-medium text-slate-700">
                        Facebook
                      </Label>
                      <Input
                        id="facebook_url"
                        type="url"
                        value={formData.facebook_url || ''}
                        onChange={(e) => setFormData({...formData, facebook_url: e.target.value})}
                        placeholder="https://facebook.com/tuoprofilo"
                        className="mt-1 h-12"
                      />
                    </div>

                    <div>
                      <Label htmlFor="instagram_url" className="text-sm font-medium text-slate-700">
                        Instagram
                      </Label>
                      <Input
                        id="instagram_url"
                        type="url"
                        value={formData.instagram_url || ''}
                        onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                        placeholder="https://instagram.com/tuoprofilo"
                        className="mt-1 h-12"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Foto Profilo (JPG, PNG)
                </Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                  {profile?.profile_image_url && (
                    <img src={profile.profile_image_url} alt="Foto profilo" className="w-24 h-24 rounded-full mx-auto mb-3 object-cover" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'photo')}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      {profile?.profile_image_url ? 'Clicca per cambiare foto' : 'Clicca per caricare foto profilo'}
                    </p>
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Carica CV (PDF, DOCX)
                </Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileUpload(e, 'cv')}
                    className="hidden"
                    id="cv-upload"
                  />
                  <label htmlFor="cv-upload" className="cursor-pointer" data-testid="cv-upload-label">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      {profile?.cv_url ? 'CV caricato - Clicca per sostituire' : 'Clicca per caricare il tuo CV'}
                    </p>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center space-x-4 pb-6 border-b border-slate-200">
                <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  <Building2 className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#344B71]">{user.name}</h2>
                  <p className="text-slate-600">{user.email}</p>
                </div>
              </div>

              <div>
                <Label htmlFor="company_name" className="text-sm font-medium text-slate-700">
                  Nome Azienda *
                </Label>
                <Input
                  id="company_name"
                  value={formData.company_name || ''}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="mt-1 h-12"
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label htmlFor="partita_iva" className="text-sm font-medium text-slate-700">
                  P.IVA / Codice Fiscale *
                </Label>
                <Input
                  id="partita_iva"
                  value={formData.partita_iva || ''}
                  onChange={(e) => setFormData({...formData, partita_iva: e.target.value})}
                  className="mt-1 h-12"
                  placeholder="es. IT12345678901"
                  data-testid="partita-iva-input"
                />
                <p className="text-xs text-slate-500 mt-1">Campo obbligatorio per la registrazione dell'azienda</p>
              </div>

              <div>
                <Label htmlFor="sector" className="text-sm font-medium text-slate-700">
                  Settore *
                </Label>
                <Select 
                  value={formData.sector} 
                  onValueChange={(value) => setFormData({...formData, sector: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="sector-select">
                    <SelectValue placeholder="Seleziona settore" />
                  </SelectTrigger>
                  <SelectContent>
                    {sectors.map((sector) => (
                      <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="company_size" className="text-sm font-medium text-slate-700">
                  Dimensione Azienda *
                </Label>
                <Select 
                  value={formData.company_size} 
                  onValueChange={(value) => setFormData({...formData, company_size: value})}
                >
                  <SelectTrigger className="mt-1 h-12" data-testid="company-size-select">
                    <SelectValue placeholder="Numero dipendenti" />
                  </SelectTrigger>
                  <SelectContent>
                    {companySizes.map((size) => (
                      <SelectItem key={size} value={size}>{size} dipendenti</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="website" className="text-sm font-medium text-slate-700">
                  Sito Web
                </Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website || ''}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://www.esempio.com"
                  className="mt-1 h-12"
                  data-testid="website-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                  Descrizione Azienda *
                </Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={4}
                  placeholder="Descrivi la tua azienda e le sue attività..."
                  className="mt-1"
                  data-testid="description-input"
                />
              </div>

              {/* Location Section for Companies */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#344B71] mb-4">Sede Legale *</h3>
                <p className="text-sm text-slate-600 mb-4">Indica la sede legale della tua azienda</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Paese *</Label>
                    <Select 
                      value={formData.legal_country || 'Italia'} 
                      onValueChange={(value) => setFormData({...formData, legal_country: value})}
                    >
                      <SelectTrigger className="mt-1 h-12">
                        <SelectValue placeholder="Seleziona paese" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Provincia *</Label>
                    <Select 
                      value={formData.legal_province || ''} 
                      onValueChange={(value) => setFormData({...formData, legal_province: value})}
                    >
                      <SelectTrigger className="mt-1 h-12">
                        <SelectValue placeholder="Seleziona provincia" />
                      </SelectTrigger>
                      <SelectContent>
                        {italianProvinces.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Comune *</Label>
                    <Input
                      value={formData.legal_city || ''}
                      onChange={(e) => setFormData({...formData, legal_city: e.target.value})}
                      placeholder="es. Milano"
                      className="mt-1 h-12"
                    />
                  </div>
                </div>

                {/* Operational Headquarters */}
                <div className="mb-4">
                  <label className="flex items-center space-x-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.has_operational_hq || false}
                      onChange={(e) => setFormData({...formData, has_operational_hq: e.target.checked})}
                      className="w-5 h-5 rounded border-slate-300 text-[#344B71] focus:ring-[#344B71]"
                    />
                    <span className="text-slate-700">Ho una sede operativa diversa dalla sede legale</span>
                  </label>
                </div>

                {formData.has_operational_hq && (
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <h4 className="text-md font-semibold text-[#344B71] mb-4">Sede Operativa</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Paese</Label>
                        <Select 
                          value={formData.operational_country || 'Italia'} 
                          onValueChange={(value) => setFormData({...formData, operational_country: value})}
                        >
                          <SelectTrigger className="mt-1 h-12 bg-white">
                            <SelectValue placeholder="Seleziona paese" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((c) => (
                              <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Provincia</Label>
                        <Select 
                          value={formData.operational_province || ''} 
                          onValueChange={(value) => setFormData({...formData, operational_province: value})}
                        >
                          <SelectTrigger className="mt-1 h-12 bg-white">
                            <SelectValue placeholder="Seleziona provincia" />
                          </SelectTrigger>
                          <SelectContent>
                            {italianProvinces.map((p) => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Comune</Label>
                        <Input
                          value={formData.operational_city || ''}
                          onChange={(e) => setFormData({...formData, operational_city: e.target.value})}
                          placeholder="es. Roma"
                          className="mt-1 h-12 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Nuovi campi contatti e social */}
              <div className="pt-4 border-t border-slate-200">
                <h3 className="text-lg font-semibold text-[#344B71] mb-4">Contatti e Social</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="contact_person" className="text-sm font-medium text-slate-700">
                      Nome Referente
                    </Label>
                    <Input
                      id="contact_person"
                      type="text"
                      value={formData.contact_person || ''}
                      onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                      placeholder="Mario Rossi"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email_contact" className="text-sm font-medium text-slate-700">
                      Email di Contatto
                    </Label>
                    <Input
                      id="email_contact"
                      type="email"
                      value={formData.email_contact || ''}
                      onChange={(e) => setFormData({...formData, email_contact: e.target.value})}
                      placeholder="contatto@azienda.com"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Telefono
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+39 123 456 7890"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div>
                    <Label htmlFor="linkedin_url" className="text-sm font-medium text-slate-700">
                      LinkedIn Aziendale
                    </Label>
                    <Input
                      id="linkedin_url"
                      type="url"
                      value={formData.linkedin_url || ''}
                      onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                      placeholder="https://www.linkedin.com/company/tuaazienda"
                      className="mt-1 h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="facebook_url" className="text-sm font-medium text-slate-700">
                        Facebook
                      </Label>
                      <Input
                        id="facebook_url"
                        type="url"
                        value={formData.facebook_url || ''}
                        onChange={(e) => setFormData({...formData, facebook_url: e.target.value})}
                        placeholder="https://facebook.com/tuaazienda"
                        className="mt-1 h-12"
                      />
                    </div>

                    <div>
                      <Label htmlFor="instagram_url" className="text-sm font-medium text-slate-700">
                        Instagram
                      </Label>
                      <Input
                        id="instagram_url"
                        type="url"
                        value={formData.instagram_url || ''}
                        onChange={(e) => setFormData({...formData, instagram_url: e.target.value})}
                        placeholder="https://instagram.com/tuaazienda"
                        className="mt-1 h-12"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Logo Aziendale (JPG, PNG)
                </Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                  {profile?.logo_url && (
                    <img src={profile.logo_url} alt="Logo" className="w-24 h-24 mx-auto mb-3 object-contain" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e, 'logo')}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      {profile?.logo_url ? 'Clicca per cambiare logo' : 'Clicca per caricare logo aziendale'}
                    </p>
                  </label>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Carica Company Profile (PDF)
                </Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, 'company_profile')}
                    className="hidden"
                    id="profile-upload"
                  />
                  <label htmlFor="profile-upload" className="cursor-pointer" data-testid="profile-upload-label">
                    <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">
                      {profile?.company_profile_url ? 'Profilo caricato - Clicca per sostituire' : 'Clicca per caricare il profilo aziendale'}
                    </p>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Verification Section - Only for professionals */}
          {user.user_type === 'professional' && (
            <div className="mt-8 p-6 bg-gradient-to-r from-[#344B71]/5 to-[#344B71]/5 rounded-2xl border border-[#344B71]/20">
              <div className="flex items-center gap-3 mb-4">
                <BadgeCheck className="w-6 h-6 text-[#344B71]" />
                <h3 className="text-lg font-semibold text-[#344B71]">Badge Professionista Verificato</h3>
              </div>
              
              {profile?.is_verified ? (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                  <BadgeCheck className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Profilo Verificato!</p>
                    <p className="text-sm text-green-600">
                      Il tuo profilo è stato verificato il {new Date(profile.verified_at).toLocaleDateString('it-IT')}
                    </p>
                    {profile.verification_documents?.length > 0 && (
                      <p className="text-xs text-green-500 mt-1">
                        Documenti: {profile.verification_documents.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              ) : profile?.verification_requested ? (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Richiesta in attesa di revisione</p>
                      <p className="text-sm text-amber-600">
                        Richiesta inviata il {new Date(profile.verification_requested_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  </div>
                  {profile.verification_request_message && (
                    <p className="text-sm text-amber-700 italic mb-3 pl-9">
                      &quot;{profile.verification_request_message}&quot;
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelVerificationRequest}
                    className="ml-9 text-amber-700 border-amber-300 hover:bg-amber-100"
                  >
                    Annulla Richiesta
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-600">
                    Richiedi la verifica del tuo profilo per ottenere il badge "Verificato" e aumentare la tua credibilità 
                    sulla piattaforma. Il nostro team verificherà le tue credenziali professionali.
                  </p>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-amber-700 text-sm font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Servizio in arrivo
                    </p>
                  </div>
                  <div className="opacity-50">
                    <Label className="text-slate-700">Messaggio per il gestore (opzionale)</Label>
                    <Textarea
                      value={verificationMessage}
                      onChange={(e) => setVerificationMessage(e.target.value)}
                      placeholder="Descrivi brevemente la tua esperienza, certificazioni o documenti che puoi fornire per la verifica..."
                      rows={3}
                      className="mt-1 rounded-xl"
                      disabled
                    />
                  </div>
                  <Button
                    disabled={true}
                    className="bg-slate-300 text-slate-500 cursor-not-allowed rounded-xl"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Richiedi Verifica (Disponibile a breve)
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 flex justify-end space-x-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#344B71] hover:bg-[#344B71] text-white px-8 rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="save-profile-button"
            >
              {saving ? 'Salvataggio...' : 'Salva Profilo'}
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ProfilePage;
